import React, { useId } from 'react';
import { ModalProps as ModalBootstrapProps } from 'react-bootstrap';

import {
  ClearCloseButton,
  ModalSizeOption,
  StyledModal,
  StyledModalBody,
  StyledModalHeader,
  StyledModalTitle,
  TitleIcon,
} from './styles';

interface CustomModalProps
  extends Omit<ModalBootstrapProps, 'show' | 'onHide' | 'size'> {
  children: React.ReactNode;
  open?: boolean;
  show?: boolean;
  onClose?: () => void;
  title?: string;
  icon?: string;
  width?: string;
  maxBodyHeight?: string | number;
  size?: ModalSizeOption;
  clear?: boolean;
  style?: React.CSSProperties;
  onHide?: () => void;
  'data-testid'?: boolean | string;
}

function CustomModal({
  children,
  open,
  onClose,
  onHide,
  title,
  icon,
  width,
  maxBodyHeight,
  size,
  clear = false,
  style,
  'data-testid': dataTestId,
  ...rest
}: CustomModalProps) {
  const labelledId = useId();
  const handleClose = onClose ?? onHide ?? (() => undefined);
  const resolvedTitle = title ?? '';

  const testId =
    typeof dataTestId === 'string'
      ? dataTestId
      : dataTestId
        ? `modal-${resolvedTitle ? resolvedTitle.replace(/\s+/g, '-').toLowerCase() : labelledId}`
        : undefined;

  return (
    <StyledModal
      show={open}
      onHide={handleClose}
      centered
      role="dialog"
      aria-modal="true"
      aria-labelledby={!clear && resolvedTitle ? labelledId : undefined}
      aria-label={clear ? resolvedTitle || undefined : undefined}
      keyboard
      autoFocus
      $width={width}
      $size={size || 'medium'}
      style={style}
      data-testid={testId}
      {...rest}
    >
      {!clear && (
        <StyledModalHeader
          closeButton
          data-testid={testId ? `${testId}-header` : undefined}
        >
          {resolvedTitle && (
            <StyledModalTitle
              id={labelledId}
              data-testid={testId ? `${testId}-title` : undefined}
            >
              {icon && (
                <TitleIcon data-testid={testId ? `${testId}-icon` : undefined}>
                  {icon}
                </TitleIcon>
              )}
              {resolvedTitle}
            </StyledModalTitle>
          )}
        </StyledModalHeader>
      )}
      {clear && (
        <ClearCloseButton
          aria-label="Fechar"
          onClick={handleClose}
          data-testid={testId ? `${testId}-close` : undefined}
        />
      )}
      <StyledModalBody
        $maxBodyHeight={maxBodyHeight}
        data-testid={testId ? `${testId}-body` : undefined}
      >
        {children}
      </StyledModalBody>
    </StyledModal>
  );
}

export default CustomModal;
